import React from "react";

import { useAppSelector } from "store";
import { selectUserInfo } from "modules/auth/authSlice";
import { selectFlattenedProducts } from "modules/customer/tools/product/productSlice";

import { Box, Button, Typography, useTheme, withStyles } from "@material-ui/core";
import { ExpandLess, ExpandMore, GetApp } from "@material-ui/icons";
import {
    DataGridPro,
    GridColDef,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarFilterButton,
    useGridApiRef
} from "@mui/x-data-grid-pro";

import Spacer from "components/Spacer";
import numberFormatter from "utils/numberFormatter";

const StyledDataGrid = withStyles(theme => ({
    root: {
        border: 'none',
        backgroundColor: theme.palette.background.paper,
        paddingTop: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        '& .MuiCheckbox-root': {
            color: theme.palette.text.primary,
            '&.Mui-checked': {
                //@ts-ignore
                color: theme.palette.tertiary.main,
            },
        },
        '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.16)',
            },
        },
        '& .MuiDataGrid-cell:focus': {
            outline: 'none'
        },
        '& .MuiDataGrid-cell:focus-within': {
            outline: 'none'
        }
    }
}))(DataGridPro);

const SalesAndHeadroomByProduct: React.FC = () => {
    const theme = useTheme();
    const userInfo = useAppSelector(selectUserInfo);
    const { data: rows, isLoading } = useAppSelector(selectFlattenedProducts);

    const columns: GridColDef[] = [
        {
            field: 'name',
            flex: 1,
            headerName: 'Product name',
            sortable: true
        },
        {
            field: 'productCategory',
            flex: 1,
            headerName: 'Product category',
            sortable: true,
            hide: true
        },
        {
            field: 'clientSourcedSales',
            type: 'number',
            flex: 1,
            headerName: `${userInfo.companyDisplayName} sourced sales`,
            sortable: true,
            renderCell: (params) => numberFormatter.toGBP(params.value ?? 0, 1)
        },
        {
            field: 'estimatedSales',
            type: 'number',
            flex: 1,
            headerName: 'Estimated sales',
            sortable: true,
            renderCell: (params) => numberFormatter.toGBP(params.value ?? 0, 1)
        },
        {
            field: 'optimisedSales',
            type: 'number',
            flex: 1,
            headerName: 'Optimised sales',
            sortable: true,
            renderCell: (params) => numberFormatter.toGBP(params.value ?? 0, 1)
        },
        {
            field: 'salesHeadroom',
            type: 'number',
            flex: 1,
            headerName: 'Headroom',
            sortable: true,
            renderCell: (params) => numberFormatter.toGBP(params.value ?? 0, 1)
        }
    ];

    const columnsWithWidth = columns.map(column => {
        if (column.type === 'singleSelect') {
            //@ts-expect-error
            column.valueOptions = Array.from(new Set(rows.map(row => row[column.field])));
        }
        return {
            ...column,
            minWidth: 150
        };
    });

    const apiRef = useGridApiRef();

    const handleExport = () => {
        apiRef.current.exportDataAsCsv({ fileName: "Dash stores export" });
    };

    const CustomExportButton = () => {
        return (
            <Button
                // @ts-ignore 
                style={{ color: theme.palette.tertiary.main }}
                startIcon={<GetApp />}
                onClick={handleExport}
                size="small"
            >
                Export
            </Button>
        );
    };

    function CustomToolbar() {
        return (
            <GridToolbarContainer style={{ paddingRight: theme.spacing(2) }}>
                <GridToolbarColumnsButton style={{ color: theme.palette.text.primary }} />
                <GridToolbarFilterButton style={{ color: theme.palette.text.primary }} />
                <Spacer />
                {/* @ts-ignore */}
                <CustomExportButton style={{ color: theme.palette.tertiary.main }} />
            </GridToolbarContainer>
        );
    }

    return (
        <>
            <Box paddingTop={1}>
                <Typography variant="h6">Sales and headroom by product</Typography>
            </Box>
            <br/>
            <StyledDataGrid
                rows={rows}
                columns={columnsWithWidth}
                checkboxSelection
                disableSelectionOnClick
                loading={isLoading}
                components={{
                    Toolbar: CustomToolbar,
                    ColumnSortedAscendingIcon: ExpandLess,
                    ColumnSortedDescendingIcon: ExpandMore
                }}
                sortingOrder={['asc', 'desc']}
                hideFooter
                apiRef={apiRef}
            />
        </>
    );
};

export default SalesAndHeadroomByProduct;
